import Singleton from './Singleton'
import axios from 'axios'
import {message} from 'antd'

export default class App extends Singleton {
  static API_URL = (document.location.hostname === 'localhost') ? `https://portal-benusk.crmcarecloud.com/dev-api/` : document.location.origin + '/api/'

  constructor() {
    super()

    axios.interceptors.response.use(
      (response) => response.data,
      (error) => {
        // Forward the original error object so components can access response status, etc.
        return Promise.reject(error)
      }
    )

    window.$app = this
    this.messageApi = null
  }

  static $toast(content, type = 'success') {
    if(window.$app && window.$app.messageApi) {
      window.$app.messageApi.open({type, content})
    } else {
      // Fallback for when context is not yet available
      message.open({type, content})
    }
  }

  static $e(content) {
    this.$toast(content, 'error')
  }

  setMessageApi(api) {
    this.messageApi = api
  }

  static isDevelopment() {
    return (document.location.hostname === 'localhost')
  }

  async $auth() {
    try {
      const response = await axios({
        method: 'GET',
        url: `${App.API_URL}status`,
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return response
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * Verify a loyalty card
   * @param {string} cardNumber - The 13-digit card number to verify
   * @returns {Promise<Object>} - The verification result with card status
   */
  async verifyCard(cardNumber, recaptcha_token) {
    try {
      // Basic validation
      if(!cardNumber || typeof cardNumber !== 'string' || !/^\d{13}$/.test(cardNumber)) {
        throw new Error('Invalid card number. It must be exactly 13 digits.')
      }

      const response = await axios({
        method: 'POST',
        url: `${App.API_URL}card/verify`,
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        },
        data: {
          card_number: cardNumber,
          recaptcha_token
        }
      })

      return response
    } catch (error) {
      console.error('Card verification failed:', error)
    }
  }

  /**
   * Send verification code to customer's email
   * @returns {Promise<Object>} - The API response
   */
  async sendVerificationCode() {
    try {

      const response = await axios({
        method: 'POST',
        url: `${App.API_URL}customer/send-code`,
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return response
    } catch (error) {
      console.error('Failed to send verification code:', error)
    }
  }

  /**
   * Verify the OTP code for a customer
   * @param {string} code - The OTP code to verify
   * @returns {Promise<Object>} - The verification result
   */
  async verifyCode(code, recaptcha_token) {
    try {

      const response = await axios({
        method: 'POST',
        url: `${App.API_URL}customer/verify-code`,
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        },
        data: {
          verification_code: code,
          recaptcha_token
        }
      })

      return response
    } catch (error) {
      console.error('OTP verification failed:', error)
    }
  }

  /**
   * Get customer data
   * @returns {Promise<Object>} - Customer data
   */
  async getCustomer() {
    try {

      const response = await axios({
        method: 'GET',
        url: `${App.API_URL}customer`,
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return response
    } catch (error) {
      console.error('Failed to get customer data:', error)
    }
  }

  /**
   * Update customer data
   * @param {Object} customerData - The customer data to update
   * @returns {Promise<Object>} - The updated customer data
   */
  async updateCustomer(customerData, recaptcha_token) {
    try {
      const response = await axios({
        method: 'PUT',
        url: `${App.API_URL}customer/update`,
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        },
        data: {
          recaptcha_token,
          customer: {
            personal_information: {
              ...customerData
            }
          }
        }
      })

      return response
    } catch (error) {
      console.error('Failed to update customer data:', error)
      throw error
    }
  }

  /**
   * Register a new customer
   * @param {string} cardNumber - The 13-digit card number to verify
   * @param {Object} customerData - The customer registration data
   * @returns {Promise<Object>} - The registration result
   */
  async registerCustomer(cardNumber, customerData, recaptcha_token) {
    try {

      const response = await axios({
        method: 'POST',
        url: `${App.API_URL}customer/register`,
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        },
        data: {
          card_number: cardNumber,
          recaptcha_token,
          customer: {
            personal_information: {
              ...customerData
            }
          }
        }
      })

      return response

    } catch (error) {
      console.error('Registration failed:', error)
      throw error
    }
  }

  async $api(request) {
    return new Promise(async (resolve, reject) => {

      try {
        const {action = 'default', headers = null} = request

        delete request.headers

        const response = await axios({
          withCredentials: true,
          url: `${App.API_URL}?action=${action}`,
          method: 'POST',
          headers: headers,
          data: {
            ...request
          }
        })

        if(response && response.hasOwnProperty('error')) reject(response.error)
        else resolve(response)

      } catch (error) {

        reject(error)

      }
    })
  }
}